import React, { useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Button from '../../../components/bootstrap/Button';
import { HeaderRight } from '../../../layout/Header/Header';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from '../../../components/bootstrap/OffCanvas';
import Alert from '../../../components/bootstrap/Alert';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import Icon from '../../../components/icon/Icon';
import ThemeContext from '../../../contexts/themeContext';
import LANG, { getLangWithKey } from '../../../lang';
import showNotification from '../../../components/extras/showNotification';
import useDarkMode from '../../../hooks/useDarkMode';
 import Popovers from '../../../components/bootstrap/Popovers';
 import { userAuth } from '../../../contexts/userContext';
 import apiService from '../../../api/apiService';
 import { NotifiContext } from '../../../contexts/NotifiContext';
 import { useLanguageSwitch } from '../../../contexts/LanguageSwitchContext';
 import { resetTourGuide } from '../../../helpers/tourGuideHelper';
import { useTourStatus } from '../../../contexts/TourStatusContext';
import './headerstlyle.css';

// eslint-disable-next-line react/prop-types
const CommonHeaderRight = ({ beforeChildren }) => {
	const languageSwitchContext = useLanguageSwitch();
	const { isLangSwitchEnabled } = languageSwitchContext || { isLangSwitchEnabled: false };
	const { t } = useTranslation();
	const { setToken, token, setReset } = useContext(userAuth);
	const { triggerManualTour } = useTourStatus();
	const location = useLocation();
	const notifiContextValue = useContext(NotifiContext);
	const [localNotiCount, setLocalNotiCount] = useState(() => {
		const stored = localStorage.getItem('notiCount');
		return stored ? parseInt(stored, 10) : 0;
	});
	const [localNotiList, setLocalNotiList] = useState([]);
	
	const notiCount = notifiContextValue?.notiCount ?? localNotiCount;
	const notiList = notifiContextValue?.notiList ?? localNotiList;
	
	const setNotiCount = (valueOrUpdater) => {
		const current = notifiContextValue?.notiCount ?? localNotiCount;
		const next =
			typeof valueOrUpdater === 'function' ? valueOrUpdater(current) : valueOrUpdater;
		if (notifiContextValue?.setNotiCount) {
			notifiContextValue.setNotiCount(next);
		} else {
			setLocalNotiCount(next);
		}
		localStorage.setItem('notiCount', next);
	};
	
	const setNotiList = (valueOrUpdater) => {
		const current = notifiContextValue?.notiList ?? localNotiList;
		const next =
			typeof valueOrUpdater === 'function' ? valueOrUpdater(current) : valueOrUpdater;
		if (notifiContextValue?.setNotiList) {
			notifiContextValue.setNotiList(next);
		} else {
			setLocalNotiList(next);
		}
	};
	
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	// const mobilev = windowWidth < 400 ? 'ab' : 'cd';

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const [currentPage, setCurrentPage] = useState(1);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);

	// Listen for localStorage changes from DashboardPage
	useEffect(() => {
		const handleStorageChange = () => {
			const stored = localStorage.getItem('notiCount');
			if (stored) {
				setLocalNotiCount(parseInt(stored, 10));
			}
		};
		
		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	const styledBtn = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	useEffect(() => {
		localStorage.setItem('theme', darkModeStatus ? 'dark' : 'light');
	}, [darkModeStatus]);

	const [offcanvasStatus, setOffcanvasStatus] = useState(false);

	const { i18n } = useTranslation();

	const changeLanguage = (lng) => {
		i18n.changeLanguage(lng);
		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
				<span>{`Language changed to ${getLangWithKey(lng)?.text}`}</span>
			</span>,
			// 'You updated the language of the site. (Only "Aside" was prepared as an example.)',
		);
	};

	/**
	 * Language attribute
	 */
	useLayoutEffect(() => {
		document.documentElement.setAttribute('lang', i18n.language);
	});


	const fetchNotification = async (pageId = 1, isLoadMore = false) => {
		if (isLoadMore) {
			setIsLoadingMore(true);
		}

		const item = { action: 'get_user_notification_list', limit: 25, page_id: pageId };

		const flechdata = await apiService(item, token, setToken, navigate);
		if (flechdata.type === 'success') {
			if (isLoadMore) {
				// Append new notifications to existing list
				setNotiList(prevList => [...prevList, ...flechdata.data]);
				setIsLoadingMore(false);
			} else {
				// Replace list (initial load)
				setNotiList(flechdata.data);
				setCurrentPage(1);
			}
			setNotiCount(flechdata.TotalRecords);
console.log('flechdata.TotalRecords', flechdata.TotalRecords);
			// Check if there are more notifications to load
			const totalLoaded = isLoadMore ? notiList.length + flechdata.data.length : flechdata.data.length;
			setHasMore(totalLoaded < flechdata.TotalRecords);
		}
	};

	const loadMoreNotifications = () => {
		if (!isLoadingMore && hasMore) {
			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
			fetchNotification(nextPage, true);
		}
	};


	const navigate = useNavigate();

	const activeTourModule = useMemo(() => {
		const pathname = location.pathname;

		if (pathname === '/' || pathname === '/dashboard') return 'Dashboard';
		if (pathname === '/Calendar') return 'Calendar';
		if (pathname === '/settings') return 'Settings::System settings';
		if (pathname === '/reports/attendance') return 'Reports::Attendance';
		if (pathname === '/reports/orders') return 'Reports::Orders';
		if (pathname === '/orders' || pathname === '/myorders') return 'Orders';
		if (pathname === '/services/list') return 'Services';
		if (pathname === '/services/category') return 'Category';
		if (pathname === '/appointment/pending' || pathname === '/myappointment/pending') {
			return 'Pending Appointments';
		}
		if (pathname === '/appointment/completed' || pathname === '/myappointment/completed') {
			return 'Completed Appointments';
		}
		if (pathname === '/appointment/cancelled' || pathname === '/myappointment/cancelled') {
			return 'Cancelled Appointments';
		}
		if (pathname === '/appointment/expired' || pathname === '/myappointment/expired') {
			return 'Expired Appointments';
		}

		return 'Dashboard';
	}, [location.pathname]);
	//const web_link = process.env.REACT_APP_WEB_LINK;
	//const data_post = { action: 'logout' };

	const delAllNotification = async () => {
		const formData = {};

		formData['notification_id'] = 'all';

		formData['action'] = 'update_user_notification_status';
		formData['notification_status'] = 'r';
		try {
			const flechdata = await apiService(formData, token, setToken, navigate);

			if (flechdata.type === 'Success' || flechdata.type === 'success') {
				setNotiList([]);
			setNotiCount(0);
			} else {
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};
	const delNotification = async (id) => {
		const formData = {};

		formData['notification_id'] = id;

		formData['action'] = 'update_user_notification_status';
		formData['notification_status'] = 'r';

		try {
			const flechdata = await apiService(formData, token, setToken, navigate);

			if (flechdata.type === 'Success' || flechdata.type === 'success') {
				setNotiList((prev) => (Array.isArray(prev) ? prev.filter((n) => n.id !== id) : prev));
				setNotiCount((prev) => {
					const next = (Number(prev) || 0) - 1;
					return next < 0 ? 0 : next;
				});
			} else {
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	const logouthandle = async () => {
		const item = { action: 'logout' };
		try {
			const flechdata = await apiService(item, token, setToken, navigate);

			if (flechdata.type === 'success' || flechdata.type === 'Success') {
				setToken({});
				setReset((p) => !p);
				localStorage.removeItem('user-auth');
				resetTourGuide(); // Reset tour guide for next login
				navigate('/');
			} else {
				console.error('Error fetching services:', flechdata.message);
			}
		} catch (error) {
			console.error('Error fetching services:', error);
		}

		// const result = await fetch(web_link, {
		// 	method: 'post',
		// 	headers: {
		// 		'Content-Type': 'application/json',
		// 		Accept: 'application/json',
		// 		'sdk-authorization': `${token.session_key}`,
		// 	},
		// 	body: JSON.stringify(data_post),
		// });
		// const req = await result.json();
		// setToken({});
		// localStorage.removeItem('user-auth');
		// navigate('/');
	};

	return (
		<HeaderRight>
			<div className='row g-3'>
				{beforeChildren}
				{/* Dark Mode */}
				{/* <div className='col-auto'>
					<Popovers trigger='hover' desc='Dark / Light mode'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
							onClick={() => setDarkModeStatus(!darkModeStatus)}
							aria-label='Toggle fullscreen'
							data-tour='dark-mode'
						/>
					</Popovers>
				</div> */}

				<div className='col-auto'>
					<Popovers trigger='hover' desc='Dark / Light mode'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
							onClick={() => setDarkModeStatus((prev) => !prev)}
							aria-label='Toggle fullscreen'
							data-tour='dark-mode'
						/>
					</Popovers>
				</div>

				{/* Tour Guide Toggle */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc={`Start ${activeTourModule} tour`}>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon='Tour'
							onClick={() => triggerManualTour(activeTourModule)}
							aria-label={`Start ${activeTourModule} tour`}
							data-tour='tour-toggle'
						/>
					</Popovers>
				</div>

				{/* Lang Selector */}
				{isLangSwitchEnabled && (
					<div className='col-auto'>
						<Dropdown>
							<DropdownToggle hasIcon={false}>
								<Button
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...styledBtn}
									icon={getLangWithKey(i18n.language)?.icon}
									aria-label='Change language'
									data-tour='lang-selector'
								/>
							</DropdownToggle>
							<DropdownMenu isAlignmentEnd data-tour='lang-selector-menu'>
								{Object.keys(LANG).map((i) => (
									<DropdownItem key={LANG[i].lng}>
										<Button
											icon={LANG[i].icon}
											onClick={() => changeLanguage(LANG[i].lng)}>
											{LANG[i].text}
										</Button>
									</DropdownItem>
								))}
							</DropdownMenu>
						</Dropdown>
					</div>
				)}

				{/*	Notifications */}
				<div className='col-auto'>
					<div className='col-auto position-relative'>
						<Button
							{...styledBtn}
							icon='Notifications'
							onClick={() => {
								setOffcanvasStatus(true);
								fetchNotification();
							}}
							aria-label='Notifications'
						/>
						{notiCount > 0 && (
							<span className='position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger'>
								{notiCount < 99 ? notiCount : '99+'}
							</span>
						)}
					</div>
				</div>

				{/*	Notifications */}
				{/* <div className='col-auto'>
					<Button
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...styledBtn}
						icon='Notifications'
						onClick={() => setOffcanvasStatus(true)}
						aria-label='Notifications'
					/>
				</div>					 */}

				{/* Quick Panel */}
				<div className='col-auto'>
					<Dropdown>
						<DropdownToggle hasIcon={false}>
							{/* eslint-disable-next-line react/jsx-props-no-spreading */}
							<Button {...styledBtn} icon='Person' aria-label='Quick menu' />
						</DropdownToggle>
						<DropdownMenu isAlignmentEnd size='lg' className='py-0 overflow-hidden'>
							<div className='row g-0'>
								<div
									className={classNames(
										'col-12',
										'p-4',
										'd-flex justify-content-center',
										'fw-medium fs-5',
										'text-info',
										'border-bottom border-info',
										'bg-lo25-info',
									)}>
									{/* <span style={{ color: 'white' }}>{token.name}</span> */}
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
										}}>
										<span style={{ color: 'white', fontSize: '16px' }}>
											{token.name}
										</span>
										<span style={{ color: 'white', fontSize: '12px' }}>
											{token.email}
										</span>
										<span
											style={{
												color: 'white',
												fontSize: '11px',
												marginTop: '2px',
											}}>
											{token.user_roles_name}
										</span>
									</div>
								</div>
								<div
									className={classNames(
										'col-12 p-4 transition-base cursor-pointer',
										// 'border-end border-bottom',
										{ 'border-dark': darkModeStatus },
									)}>
									<div
										className='d-flex flex-column align-items-center justify-content-center'
										onClick={logouthandle}>
										{/* <Icon icon='Logout' size='3x' color='info' onClick={() => logout()}/> */}
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '7px',
												color: '#e17f7f',
											}}>
											<Icon icon='Logout' size='3x' />
											<span style={{ fontWeight: '500', fontSize: '14px' }}>
												{t('Log Out')}
											</span>
										</div>
									</div>
								</div>
								{/*<div
									className={classNames(
										// 'col-6 p-4 transition-base cursor-pointer bg-light-hover',
										'col-6 p-4 transition-base cursor-pointer',
										'border-bottom',
										{ 'border-dark': darkModeStatus },
									)}>
									<div
										className='d-flex flex-column align-items-center justify-content-center'
										onClick={() => {
											navigate('/setting-pages/vender');
										}}>
										<Icon icon='Settings' size='3x' color='success' />
										<span>Settings</span>
									</div>
								</div>*/}
							</div>
						</DropdownMenu>
					</Dropdown>
				</div>

				{/*	Full Screen */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Fullscreen'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
							onClick={() => setFullScreenStatus(!fullScreenStatus)}
							aria-label='Toggle dark mode'
						/>
					</Popovers>
				</div>
			</div>

			<OffCanvas
				id='notificationCanvas'
				titleId='offcanvasExampleLabel'
				placement='end'
				className='notifications-drawer'
				isOpen={offcanvasStatus}
				setOpen={setOffcanvasStatus}>
				<OffCanvasHeader setOpen={setOffcanvasStatus}>
					<OffCanvasTitle id='offcanvasExampleLabel'>Notifications</OffCanvasTitle>
					{notiList.length > 0 && (
						<Button
							color='danger'
							icon='Delete'
							style={{ marginLeft: '100px' }}
							onClick={() => delAllNotification()}>
							Clear All
						</Button>
					)}
				</OffCanvasHeader>
				<OffCanvasBody className='notifications-drawer-body'>
					<div
						className='notifications-body'
						onScroll={(e) => {
							const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
							// Load more when user scrolls near bottom
							if (scrollHeight - scrollTop <= clientHeight + 50 && hasMore && !isLoadingMore) {
								loadMoreNotifications();
							}
						}}>
						{notiList && notiList.length > 0 ? (
							<>
								{notiList.map((item) => {
									let pathname = '';
									let color = '';
									switch (item.notify_type) {
										case 'leave_requests':
											pathname = '/myappointment/pending';
											color = 'info'
											break;
										case 'refund_requests':
											pathname = '/myappointment/cancelled';
											color = 'info'
											break;
										case 'invocie_approve':
											pathname = '/myappointment/completed';
											color = 'info'
											break;
										case 'reminder_domain_expiration':
											pathname = '/myappointment/pending';
											color = 'warning'
											break;
										case 'reminder_worker_shed':
											pathname = '/Leave';
											color = 'warning'
											break;
										case 'reminder_low_quantity':
											pathname = '/Leave';
											color = 'warning'
											break;
										case 'reminder_exp_appointments':
											pathname = '/Leave';
											color = 'warning'
											break;
										case 'invoice_reject':
											pathname = '/Leave';
											color = 'danger'
											break;
										case 'acc_suspend':
											pathname = '/Leave';
											color = 'danger'
											break;
										default:
											pathname = '/';
											color = 'info'
									}

									return (
										<Alert
											key={item.id}
											icon='NotificationsNone'
											isLight
											color={color}
											className='notification-card flex-nowrap'
											isDismissible
											onClose={() => delNotification(item.id)}>
											{/* <Link
											key={item.id}
											to={{
												pathname: pathname,
												search: `?appid=${item.appointment_code}`,
											}}
											style={{ textDecoration: 'none', display: 'block' }}
											onClick={() => {
												setOffcanvasStatus(false);
												setPendingParams(item.appointment_code);
											}}> */}
											<div className='notification-card-content'
												// onClick={() => {
												// 	if (
												// 		item.notify_type === 'add_appointment' ||
												// 		item.notify_type === 'cancel_appointment' ||
												// 		item.notify_type === 'complete_appointment' ||
												// 		item.notify_type === 'reminder'
												// 	) {
												// 		navigate(
												// 			`${pathname}?appid=${item.booking_code}&notify_id=${item.id}`,
												// 		);
												// 	} else if (
												// 		item.notify_type === 'leave_approved' ||
												// 		item.notify_type === 'leave_rejected'
												// 	) {
												// 		navigate(
												// 			`${pathname}?appid=${item.leave_id}&notify_id=${item.id}`,
												// 		);
												// 	}
												// 	// navigate(
												// 	// 	`${pathname}?appid=${item.booking_code}&notify_id=${item.id}`,
												// 	// );
												// 	setOffcanvasStatus(false);
												// }}
												>
												<strong>{item.notify_title}</strong>
												<p>{item.notify_desc}</p>
												<small
													style={{
														textAlign: 'right',
													}}>
													{/* <small
												style={{
													marginLeft: mobilev == 'ab' ? '110px' : '150px',
												}}> */}
													{item.created_at}
												</small>
											</div>
											{/* </Link> */}
										</Alert>
									);
								})}

								{/* Loading indicator when loading more notifications */}
								{isLoadingMore && (
									<div style={{
										textAlign: 'center',
										padding: '20px',
										color: '#6c757d'
									}}>
										Loading more notifications...
									</div>
								)}

								{/* Show "No more notifications" when all are loaded */}
								{!hasMore && notiList.length > 0 && (
									<div style={{
										textAlign: 'center',
										padding: '20px',
										color: '#6c757d',
										fontSize: '14px'
									}}>
										No more notifications
									</div>
								)}
							</>
						) : (
							<div className='notifications-empty'>No notifications</div>
						)}
					</div>
				</OffCanvasBody>
			</OffCanvas>
		</HeaderRight>
	);
};
CommonHeaderRight.propTypes = {
	beforeChildren: PropTypes.node,
	afterChildren: PropTypes.node,
};
CommonHeaderRight.defaultProps = {
	beforeChildren: null,
	afterChildren: null,
};

export default CommonHeaderRight;
